import axios, {
  AxiosHeaders,
  CanceledError,
  HttpStatusCode
} from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import UpdateEntityAgreementAPIRequest from "legal-entities/entities/entity-agreement/api/request-contracts/update-entity-agreement-api-request";
import EntityAgreement from "legal-entities/entities/entity-agreement/entity-agreement";
import AuthInfoAPIResponse from "users/entities/user/api/response-contracts/auth-info-api-response";
import { AuthResponse } from "users/entities/user/api/auth-email-api-service";
import Session from "users/session/session";
import EntityAgreementAPIResponse from "./response-contracts/entity-agreement-api-response";

export default class EntityAgreementAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create EntityAgreementAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getEntityAgreementById(id: Guid, abortController?: AbortController): Promise<EntityAgreement> {
    const url = new URL(`/legal-entities/entity-agreements/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      const agreement: EntityAgreementAPIResponse = Object.assign(
        new EntityAgreementAPIResponse(),
        response.data
      );
      return agreement.deserialize();
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(id);
      if (error instanceof CanceledError)
        throw error;
      throw new EntityAgreementAPIServiceError("getEntityAgreementById", error);
    }
  }

  async getEntityAgreementsByIds(
    ids: Guid[],
    abortController?: AbortController
  ): Promise<EntityAgreement[]> {
    let url = new URL(
      "/legal-entities/entity-agreements/get-by-ids",
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.headers,
          signal: abortController?.signal
        }
      );
      return response.data.map((workAgreementData: Partial<EntityAgreementAPIResponse>) =>
        Object.assign(
          new EntityAgreementAPIResponse(),
          workAgreementData
        )
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidEntityAgreementRequestError(error.message);
      if (error instanceof CanceledError)
        throw error;
      throw new EntityAgreementAPIServiceError(
        "getEntityAgreementsByIds",
        error
      );
    }
  }

  async getEntityAgreementTerms(agreement: EntityAgreement, abortController: AbortController): Promise<Blob> {
    const url = new URL(`/legal-entities/agreements/${agreement.id.value}/terms`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(
        url.toString(),
        {
          headers: this.headers,
          responseType: "blob",
          signal: abortController?.signal
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden)
        throw new UnauthorizedEntityAgreementAccessError(agreement.id);
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(agreement.id);
      if (error instanceof CanceledError)
        throw error;
      throw new EntityAgreementAPIServiceError("getEntityAgreementTerms", error, agreement);
    }
  }

  async updateEntityAgreement(oldAgreement: EntityAgreement, updatedAgreement: EntityAgreement): Promise<EntityAgreement> {
    const url = new URL(`/legal-entities/entity-agreements/${oldAgreement.id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateEntityAgreementAPIRequest(oldAgreement, updatedAgreement).payload,
        {
          headers: this.headers.concat({ 'Content-Type': 'application/json' })
        }
      );
      const responseData: EntityAgreementAPIResponse = Object.assign(
        new EntityAgreementAPIResponse(),
        response.data
      );
      return responseData.deserialize();
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(oldAgreement.id);
      throw new EntityAgreementAPIServiceError("updateEntityAgreement", error, oldAgreement);
    }
  }

  async acceptEntityAgreement(entityAgreement: EntityAgreement, isClient: boolean = true, isVendor: boolean = false): Promise<AuthResponse> {
    const url = new URL(`/legal-entities/entity-agreements/${entityAgreement.id.value}/accept`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const apiResponse = await axios.post(
        url.toString(),
        {
          isClient: isClient,
          isVendor: isVendor
        },
        {
          headers: this.headers.concat({ 'Content-Type': 'application/json' })
        }
      );
      const responseData: AuthInfoAPIResponse = Object.assign(
        new AuthInfoAPIResponse(),
        apiResponse.data
      );
      return responseData.deserialize();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new EntityAgreementNotFoundError(entityAgreement.id);
      throw new EntityAgreementAPIServiceError("acceptEntityAgreement", error, entityAgreement);
    }
  }
}

export class UnauthorizedEntityAgreementAccessError extends Error {
  constructor(id: Guid) {
    super(`You are not permitted to view this agreement: ${id}`);
  }
}

export class EntityAgreementNotFoundError extends Error {
  constructor(id: Guid) {
    super(`Unable to find agreement by id: ${id}`);
  }
}

export class InvalidEntityAgreementError extends Error { }

export class InvalidEntityAgreementRequestError extends Error { }

export class EntityAgreementAPIServiceError extends Error {
  methodName: string;
  error: unknown;
  agreement?: EntityAgreement;
  constructor(methodName: string, error: unknown, agreement?: EntityAgreement) {
    super(`Error in EntityAgreementAPIService.${methodName}: ${error instanceof Error ? error.message : String(error)}`);
    this.methodName = methodName;
    this.error = error;
    this.agreement = agreement;
  }
}
